import { ProposalRequest } from '../types';

export interface ProgressUpdate {
  type: 'progress' | 'generation_started' | 'generation_complete' | 'error' | 'scraping_complete' | 'cancelled' | 'status';
  step?: string;
  message: string;
  progress?: number;
  current?: number;
  total?: number;
  data?: any;
  request_id?: string;
}

export interface GenerationStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
}

export class ProposalWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private pongTimeout: NodeJS.Timeout | null = null;
  private onProgressCallback?: (update: ProgressUpdate) => void;
  private onCompleteCallback?: (data: any) => void;
  private onErrorCallback?: (error: string) => void;
  private onStepsUpdateCallback?: (steps: GenerationStep[]) => void;
  private hasSeenScraping = false;
  private lastRequest: ProposalRequest | null = null;
  private lastAuthToken: string | null = null;
  private isReconnecting = false;
  private connectionId: string | null = null;
  private lastRequestId: string | null = null;

  constructor(private baseUrl: string = process.env.REACT_APP_API_URL || 'http://localhost:9110') {
    // Convert HTTP URL to WebSocket URL
    this.baseUrl = this.baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
  }

  /**
   * Connect to WebSocket and generate proposal with real-time progress
   */
  async generateWithProgress(
    request: ProposalRequest,
    authToken: string,
    onProgress: (update: ProgressUpdate) => void,
    onStepsUpdate: (steps: GenerationStep[]) => void,
    onComplete: (data: any) => void,
    onError: (error: string) => void
  ): Promise<void> {
    this.onProgressCallback = onProgress;
    this.onStepsUpdateCallback = onStepsUpdate;
    this.onCompleteCallback = onComplete;
    this.onErrorCallback = onError;

    if (!authToken) {
      throw new Error('Authentication token required for proposal generation');
    }

    this.lastRequest = request;  // Store request for potential reconnection
    this.lastAuthToken = authToken;

    return new Promise((resolve, reject) => {
      try {
        // Close existing connection if any
        this.disconnect();

        // Create WebSocket connection
        const wsUrl = `${this.baseUrl}/ws/proposal/generate`;
        console.log('Connecting to WebSocket:', wsUrl);
        this.ws = new WebSocket(wsUrl, ['bearer', authToken]);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.connectionId = `conn_${Date.now()}`;

          if (this.isReconnecting) {
            // If reconnecting, notify user
            onProgress({
              type: 'progress',
              message: 'Reconnected to server',
              progress: 0
            });
            this.isReconnecting = false;
          }

          this.reconnectAttempts = 0;
          this.hasSeenScraping = false; // Reset scraping flag for new generation

          // Start ping interval to keep connection alive
          this.startPingInterval();

          // Send generation request
          this.sendMessage({
            type: 'generate',
            data: {
              user_ids: request.user_ids,
              client_url: request.client_url,
              client_context: request.client_context,
              scraped_content: request.scraped_content,  // Send cached scraped content
              extracted_info: request.extracted_info,    // Send cached extracted info
              format_type: request.format_type,
              client_name: request.client_name,
              user_info: request.user_info
            }
          });
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.onErrorCallback?.('WebSocket connection error');
          this.attemptReconnect();
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.stopPingInterval();
          
          if (!event.wasClean) {
            this.attemptReconnect();
          }
        };

        resolve();
      } catch (error) {
        console.error('Failed to establish WebSocket connection:', error);
        reject(error);
      }
    });
  }

  private handleMessage(data: any): void {
    // DAG-003 (Round 6 #105): the previous `console.log('WebSocket
    // message received:', data)` dumped the full inbound payload to
    // the browser console. generation-complete messages carry
    // generated profile bodies + governed proposal data, so the log
    // leaked PD/client content into console history and any
    // client-side log-collection layered on top. Log only the
    // non-sensitive shape — type + request id — instead.
    if (data?.type) {
      console.debug(`WS msg type=${data.type} req=${data.request_id ?? 'n/a'}`);
    }

    // Store request ID if provided
    if (data.request_id) {
      this.lastRequestId = data.request_id;
    }

    // Map progress to generation steps
    if (data.type === 'progress' || data.type === 'generation_started') {
      const steps = this.mapProgressToSteps(data);
      this.onStepsUpdateCallback?.(steps);
    }

    switch (data.type) {
      case 'generation_started':
        this.onProgressCallback?.(data);
        break;

      case 'progress':
        this.onProgressCallback?.(data);
        break;

      case 'scraping_complete':
        this.onProgressCallback?.(data);
        break;

      case 'generation_complete':
        this.onCompleteCallback?.(data.data);
        this.lastRequest = null;  // Clear stored request
        this.lastAuthToken = null;
        this.disconnect();
        break;

      case 'error':
        this.onErrorCallback?.(data.message);
        this.lastRequest = null;  // Clear stored request on error
        this.lastAuthToken = null;
        this.disconnect();
        break;

      case 'cancelled':
        this.onProgressCallback?.(data);
        this.lastRequest = null;  // Clear stored request on cancellation
        this.lastAuthToken = null;
        break;

      case 'pong':
        // Keep-alive response - reset pong timeout
        console.log('Received pong from server - connection healthy');
        this.resetPongTimeout();
        break;

      case 'ping':
        // Server is pinging us, respond immediately
        console.log('Received ping from server, sending pong immediately');
        this.sendMessage({ type: 'pong' });
        // Also reset our own timeout since we know the connection is alive
        this.resetPongTimeout();
        break;

      case 'status':
        // Connection status update
        console.log('Connection status:', data.data);
        break;

      default:
        console.warn('Unknown WebSocket message type:', data.type);
    }
  }

  private mapProgressToSteps(data: ProgressUpdate): GenerationStep[] {
    // Determine if we're showing scraping step based on the progress updates
    const hasScraping = data.step === 'scraping' || data.step === 'scraping_complete';
    
    // Build steps array conditionally
    const baseSteps: GenerationStep[] = [];
    
    // Only add scraping step if we've seen scraping progress
    if (hasScraping || this.hasSeenScraping) {
      this.hasSeenScraping = true;
      baseSteps.push({ id: 'scrape', label: 'Processing client website', status: 'pending' });
    }
    
    baseSteps.push(
      { id: 'fetch', label: 'Fetching user profiles', status: 'pending' },
      { id: 'analyze', label: 'Analyzing profiles', status: 'pending' },
      { id: 'generate', label: 'Generating tailored bios', status: 'pending' },
      { id: 'format', label: 'Formatting results', status: 'pending' }
    );
    
    // Define the step progression order
    const stepOrder = ['scraping', 'scraping_complete', 'fetching', 'analyzing', 'generating', 'formatting'];
    const currentStepIndex = stepOrder.indexOf(data.step || '');
    
    // Update step statuses based on current progress
    if (data.step) {
      baseSteps.forEach((step) => {
        if (step.id === 'scrape') {
          if (data.step === 'scraping') {
            step.status = 'active';
            step.label = data.message || 'Processing client website';
          } else if (currentStepIndex > stepOrder.indexOf('scraping')) {
            step.status = 'completed';
          }
        } else if (step.id === 'fetch') {
          if (data.step === 'fetching') {
            step.status = 'active';
            // Show progress if available
            if (data.current !== undefined && data.total !== undefined && data.current > 0) {
              step.label = `Fetching user profiles (${data.current}/${data.total})`;
            } else {
              step.label = data.message || 'Fetching user profiles';
            }
          } else if (currentStepIndex > stepOrder.indexOf('fetching')) {
            step.status = 'completed';
          } else if (currentStepIndex < stepOrder.indexOf('fetching') && currentStepIndex >= 0) {
            step.status = 'pending';
          }
        } else if (step.id === 'analyze') {
          if (data.step === 'analyzing') {
            step.status = 'active';
            // Show progress if available
            if (data.current !== undefined && data.total !== undefined && data.current > 0) {
              step.label = `Analyzing profiles (${data.current}/${data.total})`;
            } else {
              step.label = data.message || 'Analyzing profiles';
            }
          } else if (currentStepIndex > stepOrder.indexOf('analyzing')) {
            step.status = 'completed';
          } else if (currentStepIndex < stepOrder.indexOf('analyzing') && currentStepIndex >= 0) {
            step.status = 'pending';
          }
        } else if (step.id === 'generate') {
          if (data.step === 'generating') {
            step.status = 'active';
            // Update label with progress for generating step
            if (data.current !== undefined && data.total !== undefined && data.current > 0) {
              step.label = `Generating tailored bios (${data.current}/${data.total})`;
            } else {
              step.label = data.message || 'Generating tailored bios';
            }
          } else if (currentStepIndex > stepOrder.indexOf('generating')) {
            step.status = 'completed';
          } else if (currentStepIndex < stepOrder.indexOf('generating') && currentStepIndex >= 0) {
            step.status = 'pending';
          }
        } else if (step.id === 'format') {
          if (data.step === 'formatting') {
            step.status = 'active';
            step.label = data.message || 'Formatting results';
          } else if (currentStepIndex > stepOrder.indexOf('formatting')) {
            step.status = 'completed';
          } else if (currentStepIndex < stepOrder.indexOf('formatting') && currentStepIndex >= 0) {
            step.status = 'pending';
          }
        }
      });
    }

    // Special handling for completion
    if (data.type === 'generation_complete') {
      baseSteps.forEach(step => step.status = 'completed');
    }

    return baseSteps;
  }

  private sendMessage(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        console.log('Sending ping to server');
        this.sendMessage({ type: 'ping' });
        // Set timeout for pong response
        this.setPongTimeout();
      }
    }, 25000); // Ping every 25 seconds (slightly less than server's 30s interval)
  }

  private setPongTimeout(): void {
    // Clear any existing timeout first
    this.resetPongTimeout();
    this.pongTimeout = setTimeout(() => {
      console.warn('Pong timeout - connection may be dead');
      // Don't immediately close, try sending another ping first
      if (this.isConnected()) {
        console.log('Trying one more ping before disconnecting...');
        this.sendMessage({ type: 'ping' });
        // Give it one more chance with a longer timeout for heavy processing
        this.pongTimeout = setTimeout(() => {
          console.error('Connection confirmed dead, closing WebSocket');
          this.ws?.close();
          this.attemptReconnect();
        }, 15000); // Increased to 15 seconds for second attempt
      }
    }, 30000);  // Increased to 30 seconds for initial timeout (very lenient for long operations)
  }

  private resetPongTimeout(): void {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.resetPongTimeout();
  }

  private attemptReconnect(): void {
    if (this.isReconnecting) {
      return;  // Already attempting to reconnect
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.onErrorCallback?.('Failed to reconnect to server. Please refresh and try again.');
      this.lastRequest = null;
      this.lastAuthToken = null;
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    // Notify user of reconnection attempt
    this.onProgressCallback?.({
      type: 'progress',
      message: `Connection lost. Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
      progress: 0
    });

    this.reconnectTimeout = setTimeout(async () => {
      // If we have a stored request, try to resume the generation
      if (this.lastRequest && this.lastAuthToken && this.onProgressCallback && this.onStepsUpdateCallback &&
          this.onCompleteCallback && this.onErrorCallback) {
        try {
          await this.generateWithProgress(
            this.lastRequest,
            this.lastAuthToken,
            this.onProgressCallback,
            this.onStepsUpdateCallback,
            this.onCompleteCallback,
            this.onErrorCallback
          );
        } catch (error) {
          console.error('Reconnection failed:', error);
          this.isReconnecting = false;
          this.attemptReconnect();
        }
      } else {
        // No stored request, just notify the user
        this.isReconnecting = false;
        this.onErrorCallback?.('Connection lost. Please try generating again.');
        this.lastRequest = null;
        this.lastAuthToken = null;
      }
    }, delay);
  }

  /**
   * Disconnect WebSocket connection
   */
  disconnect(): void {
    this.stopPingInterval();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }
  }

  /**
   * Cancel ongoing generation
   */
  cancelGeneration(): void {
    if (this.isConnected()) {
      this.sendMessage({ type: 'cancel' });
    }
    this.lastRequest = null;
  }

  /**
   * Get connection status
   */
  getStatus(): void {
    if (this.isConnected()) {
      this.sendMessage({ type: 'status' });
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
